import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./useAuth";
import { takePostLoginPath } from "../utils/postLoginPath";

/**
 * Consumes the query string the backend redirects back to after
 * apps.auth.microsoft_auth / apps.auth.google_auth issues a token
 * (?sso_token=...&sso_refresh=...&status=...&user_id=...), or forwards an
 * ?sso_error=... to the login page so LoginForm can show it.
 *
 * Runs once per page load — after loginWithSsoTokens resolves the query
 * string is stripped via history.replaceState so a refresh doesn't replay it.
 */
export function useSsoCallback() {
  const { loginWithSsoTokens, finishSignIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("sso_error");
    if (error) {
      // Already on /login (e.g. this effect re-running there, or a refresh) —
      // leave the param alone for LoginForm to read instead of redirecting
      // again, which would otherwise loop forever.
      if (window.location.pathname !== "/login") {
        window.location.replace(`/login?sso_error=${encodeURIComponent(error)}`);
      }
      return;
    }

    const accessToken = params.get("sso_token");
    if (!accessToken) return;
    const refreshToken = params.get("sso_refresh");
    const session = params.get("session");

    let returnTo = null;
    loginWithSsoTokens({ accessToken, refreshToken, session })
      .then(() => {
        returnTo = takePostLoginPath();
      })
      .finally(() => {
        params.delete("sso_token");
        params.delete("sso_refresh");
        params.delete("status");
        params.delete("user_id");
        params.delete("session");
        const query = params.toString();
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${query ? `?${query}` : ""}`,
        );
        // Signed in on the way to somewhere specific (a scanned vault QR):
        // go there. Only the dashboard lifts the "Signing you in…" overlay
        // itself, so lift it here — the page shows its own loading state.
        if (returnTo) {
          navigate(returnTo, { replace: true });
          finishSignIn();
        }
      });
    // Only ever run once per page load — the query string is the only signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useSsoCallback;
